/**
 * AfterCare — App bootstrap.
 * Initializes database, router, voice system, and service worker.
 * All processing is 100% offline — zero network calls at runtime.
 */

import { getDb, getPatientName } from './core/db';
import { createNavBar } from './ui/components/nav-bar';
import { showNotification } from './ui/components/notification-card';
import { renderHomeScreen } from './ui/screens/home-screen';
import { renderMedListScreen } from './ui/screens/med-list-screen';
import { renderDrugInfoScreen } from './ui/screens/drug-info-screen';
import { renderScheduleScreen } from './ui/screens/schedule-screen';
import { renderSetupScreen } from './ui/screens/setup-screen';
import { renderCameraScreen } from './ui/screens/camera-screen';
import { renderChatScreen } from './ui/screens/chat-screen';
import { renderEmrScreen } from './ui/screens/emr-screen';
import { STTBridge } from './voice/stt-bridge';
import { speak, stop as stopSpeech, isSpeaking, speakGreeting, hasAudioPending } from './voice/tts';
import { showInstallPrompt } from './ui/components/install-prompt';
import { setupVoiceResponder } from './voice/voice-responder';
import { initLang, t, getLang, getLangMeta } from './i18n';
import { initLLM, getLLMStatus } from './core/llm-engine';
import { init as initEmbedding } from './core/embedding-engine';
import { initKokoroTTS, getKokoroStatus } from './voice/kokoro-tts';
// model-download-progress UI removed — LLM downloads silently in background
import { seedDemoEmr } from './core/emr-store';

// -----------------------------------------------------------------------
// Globals
// -----------------------------------------------------------------------

let sttBridge: STTBridge | null = null;
let currentScreen = '';

// -----------------------------------------------------------------------
// Router
// -----------------------------------------------------------------------

/**
 * Simple hash-based router.
 * Supported routes:
 *   #home, #med-list, #schedule, #setup, #pill-camera, #drug/:id
 */
function getScreenContainer(): HTMLElement {
  let el = document.getElementById('screen-container');
  if (!el) {
    el = document.createElement('main');
    el.id = 'screen-container';
    el.setAttribute('role', 'main');
    el.setAttribute('aria-live', 'polite');
    const app = document.getElementById('app')!;
    app.appendChild(el);
  }
  return el;
}

function parseRoute(): { screen: string; param?: string } {
  const hash = window.location.hash.replace(/^#\/?/, '') || 'home';

  // Handle #drug/:id
  if (hash.startsWith('drug/')) {
    const drugId = decodeURIComponent(hash.slice(5));
    return { screen: 'drug-info', param: drugId };
  }

  return { screen: hash };
}

async function navigateTo(screen: string, param?: string): Promise<void> {
  // Stop any playing voice/audio when leaving current screen
  stopSpeech();

  const container = getScreenContainer();
  currentScreen = screen;

  // Update nav bar active state
  if (navBarApi) {
    navBarApi.setActive(screen);
  }

  try {
    switch (screen) {
      case 'home':
        await renderHomeScreen(container);
        break;
      case 'med-list':
        await renderMedListScreen(container);
        break;
      case 'drug-info':
        if (param) {
          await renderDrugInfoScreen(container, param);
        }
        break;
      case 'schedule':
        await renderScheduleScreen(container);
        break;
      case 'setup':
        renderSetupScreen(container);
        break;
      case 'pill-camera':
        await renderCameraScreen(container);
        break;
      case 'chat':
        await renderChatScreen(container);
        break;
      case 'emr':
        await renderEmrScreen(container);
        break;
      default:
        await renderHomeScreen(container);
        break;
    }
  } catch (err) {
    console.error('Navigation error:', err);
    container.textContent = '';
    const errorMsg = document.createElement('div');
    errorMsg.className = 'screen text-center';
    errorMsg.style.paddingTop = 'var(--space-2xl)';

    const errorTitle = document.createElement('h2');
    errorTitle.textContent = t('error.somethingWrong');
    errorMsg.appendChild(errorTitle);

    const errorDetail = document.createElement('p');
    errorDetail.className = 'text-muted mt-md';
    errorDetail.textContent = t('error.tryAgain');
    errorMsg.appendChild(errorDetail);

    const homeBtn = document.createElement('button');
    homeBtn.className = 'btn btn-primary mt-lg';
    homeBtn.textContent = t('error.goHome');
    homeBtn.addEventListener('click', () => {
      window.location.hash = '#home';
    });
    errorMsg.appendChild(homeBtn);

    container.appendChild(errorMsg);
  }

  // Scroll to top on navigation
  window.scrollTo(0, 0);

  // Move focus to main content for screen reader users
  container.focus();
}

// -----------------------------------------------------------------------
// Microphone permission — request once at startup
// -----------------------------------------------------------------------

/**
 * Request microphone permission early so the user doesn't get prompted
 * every time they press the voice button. On mobile browsers, this
 * persists across page loads once granted.
 */
async function requestMicPermission(): Promise<void> {
  try {
    // Check if permission API is available first
    if (navigator.permissions) {
      const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      if (result.state === 'granted') {
        console.log('[AfterCare] Microphone already granted');
        return;
      }
    }
    // Request access — this creates a one-time prompt, then it's saved
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // Immediately release the stream — we just needed the permission grant
    stream.getTracks().forEach((t) => t.stop());
    console.log('[AfterCare] Microphone permission granted');
  } catch (err) {
    // User denied or hardware unavailable — voice will still work via text fallback
    console.warn('[AfterCare] Microphone permission not granted:', err);
  }
}

// -----------------------------------------------------------------------
// Voice system integration
// -----------------------------------------------------------------------

async function initVoiceSystem(): Promise<void> {
  try {
    sttBridge = new STTBridge();
    await sttBridge.init();
    console.log('Voice system initialized');
  } catch (err) {
    console.warn('Voice system failed to initialize:', err);
    // Voice is optional — app works without it
  }
}

function setupVoiceListeners(): void {
  document.addEventListener('aftercare:voice-start', async () => {
    if (!sttBridge || !sttBridge.isReady()) {
      showNotification(t('error.voiceNotReady'), 'warning');
      document.dispatchEvent(new CustomEvent('aftercare:voice-error'));
      return;
    }
    try {
      await sttBridge.startListening();
    } catch (err) {
      console.error('Failed to start listening:', err);
      showNotification(t('error.micAccess'), 'error');
      document.dispatchEvent(new CustomEvent('aftercare:voice-error'));
    }
  });

  document.addEventListener('aftercare:voice-stop', async () => {
    if (!sttBridge) {
      document.dispatchEvent(new CustomEvent('aftercare:voice-idle'));
      return;
    }
    try {
      const text = await sttBridge.stopListening();
      document.dispatchEvent(
        new CustomEvent('aftercare:transcript', { detail: { text } }),
      );
      document.dispatchEvent(new CustomEvent('aftercare:voice-idle'));
    } catch (err) {
      console.error('Transcription failed:', err);
      showNotification(t('error.notUnderstood'), 'warning');
      document.dispatchEvent(new CustomEvent('aftercare:voice-error'));

      // Reset to idle after brief error display
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('aftercare:voice-idle'));
      }, 2000);
    }
  });
}

// -----------------------------------------------------------------------
// Service worker registration
// -----------------------------------------------------------------------

async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;

  try {
    const reg = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });
    console.log(`[AfterCare] SW registered (app v${__APP_VERSION__}, build ${__BUILD_ID__})`);

    // When a new SW is found, auto-reload to get the latest version
    // BUT only if there was already a controlling SW (i.e. this is an UPDATE,
    // not the very first install — first install doesn't need a reload and
    // would cause the splash animation to play twice)
    reg.addEventListener('updatefound', () => {
      const newWorker = reg.installing;
      if (!newWorker) return;

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'activated' && navigator.serviceWorker.controller) {
          console.log('[AfterCare] New version available, reloading...');
          window.location.reload();
        }
      });
    });

    // Also check for updates periodically (every 5 minutes)
    setInterval(() => { reg.update().catch(() => {}); }, 5 * 60 * 1000);
  } catch (err) {
    console.warn('Service worker registration failed:', err);
  }
}

// -----------------------------------------------------------------------
// Navigation bar
// -----------------------------------------------------------------------

let navBarApi: { element: HTMLElement; setActive: (id: string) => void; updateLabels: () => void } | null = null;

function setupNavBar(): void {
  navBarApi = createNavBar((screen: string) => {
    window.location.hash = '#' + screen;
  });

  const app = document.getElementById('app')!;
  app.appendChild(navBarApi.element);

  // Update nav labels when language changes
  document.addEventListener('aftercare:lang-change', () => {
    if (navBarApi) navBarApi.updateLabels();
  });
}

// -----------------------------------------------------------------------
// Floating mute / stop-speaker button
// -----------------------------------------------------------------------

function createMuteButton(): void {
  const btn = document.createElement('button');
  btn.id = 'mute-btn';

  // Read persisted mute state
  let muted = false;
  try { muted = localStorage.getItem('aftercare:muted') === 'true'; } catch { /* ignore */ }

  function updateButton(): void {
    btn.textContent = muted ? '\uD83D\uDD07' : '\uD83D\uDD0A'; // 🔇 or 🔊
    btn.setAttribute('aria-label', muted ? 'Unmute audio' : 'Mute audio');
    btn.setAttribute('aria-pressed', String(muted));
  }

  btn.style.cssText = `
    position: fixed;
    top: 16px;
    right: 16px;
    z-index: 2000;
    width: 40px;
    height: 40px;
    border-radius: 9999px;
    border: none;
    background: rgba(0,0,0,0.35);
    color: #fff;
    font-size: 18px;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s ease, transform 0.15s ease;
    touch-action: manipulation;
  `;

  updateButton();

  btn.addEventListener('click', () => {
    muted = !muted;
    try { localStorage.setItem('aftercare:muted', String(muted)); } catch { /* ignore */ }
    updateButton();
    if (muted) {
      stopSpeech();
    }
  });

  document.body.appendChild(btn);
}

// -----------------------------------------------------------------------
// App init
// -----------------------------------------------------------------------

let splashRemoved = false;

function removeSplash(): void {
  if (splashRemoved) return;
  splashRemoved = true;
  const el = document.getElementById('loading-screen');
  if (!el) return;
  el.classList.add('fade-out');
  setTimeout(() => el.remove(), 600);
}

async function initApp(): Promise<void> {
  // SAFETY: force-remove splash after 6s even if init hangs
  const loadingSafety = setTimeout(removeSplash, 6000);

  // Initialize language preference
  initLang();

  // Initialize database
  const db = await getDb();
  if (!db) {
    console.log('[AfterCare] Running in localStorage-only mode (WASM unavailable)');
  }

  // Seed demo EMR data
  seedDemoEmr();

  // Set up navigation bar
  setupNavBar();

  // Set up voice event listeners + response handler
  setupVoiceListeners();
  setupVoiceResponder();

  // Route to initial screen
  const { screen, param } = parseRoute();
  await navigateTo(screen, param);

  // Remove splash after 1.5s of display (animations finish ~1.3s, hold for readability)
  clearTimeout(loadingSafety);
  setTimeout(removeSplash, 2000);

  // Listen for hash changes
  window.addEventListener('hashchange', async () => {
    const route = parseRoute();
    await navigateTo(route.screen, route.param);
  });

  // Delay microphone permission until after splash + greeting
  setTimeout(() => {
    requestMicPermission();
  }, 6000);

  // Initialize voice system in background (do not block UI)
  initVoiceSystem().catch((err) => {
    console.warn('Background voice init failed:', err);
  });

  // Register service worker for offline caching
  registerServiceWorker().catch((err) => {
    console.warn('SW registration failed:', err);
  });

  // Initialize embedding engine, LLM, and Kokoro TTS in background (non-blocking)
  initEmbedding().then(() => {
    console.log('[AfterCare] Embedding engine ready');
  }).catch((err) => {
    console.warn('[AfterCare] Embedding init failed:', err);
  });

  initLLM().then(() => {
    console.log(`[AfterCare] LLM ready (status: ${getLLMStatus()})`);
  }).catch((err) => {
    console.warn('[AfterCare] LLM init failed:', err);
  });

  initKokoroTTS().then(() => {
    console.log(`[AfterCare] Kokoro TTS: ${getKokoroStatus()}`);
  }).catch((err) => {
    console.warn('[AfterCare] Kokoro TTS init failed (Web Speech API fallback active):', err);
  });

  // --- Floating mute/stop speaker button ---
  createMuteButton();

  // --- Voice greeting — hybrid approach ---
  // PRIMARY:  Pre-recorded MP3 (high quality, works on iOS without gesture race)
  // FALLBACK: Web Speech API (used if MP3 file is missing or fails to load)
  const patientName = getPatientName();
  const hour = new Date().getHours();
  let greetingKey = 'home.greeting.morning';
  if (hour < 5) greetingKey = 'home.greeting.night';
  else if (hour < 12) greetingKey = 'home.greeting.morning';
  else if (hour < 17) greetingKey = 'home.greeting.afternoon';
  else if (hour < 21) greetingKey = 'home.greeting.evening';
  else greetingKey = 'home.greeting.night';

  const timeGreeting = t(greetingKey);

  // Fallback text used only if the MP3 fails — uses current language
  const greetingFallback = patientName
    ? t('greeting.withName', { greeting: timeGreeting, name: patientName })
    : t('greeting.withoutName', { greeting: timeGreeting });

  // Wait for splash to finish, then play greeting (first visit only)
  setTimeout(() => {
    const GREETING_KEY = 'aftercare:greetingCount';
    const greetingCount = parseInt(localStorage.getItem(GREETING_KEY) || '0', 10);
    localStorage.setItem(GREETING_KEY, String(greetingCount + 1));
    const shouldGreet = greetingCount === 0; // Only greet on very first visit

    if (shouldGreet) {
      speakGreeting(greetingFallback, patientName).catch(() => {
        console.log('[AfterCare] All greeting audio failed — silent startup');
      });
    }

    // After a short delay, check if autoplay was blocked. If so, show a
    // subtle "tap to begin" overlay that captures the FIRST user gesture
    // specifically for the greeting — before the user taps a navigation
    // button that might open a new tab or navigate away.
    setTimeout(() => {
      if (!hasAudioPending()) return; // Autoplay worked — no overlay needed

      console.log('[AfterCare] Autoplay blocked — showing tap-to-begin overlay');

      const overlay = document.createElement('div');
      overlay.id = 'tap-to-begin';
      overlay.setAttribute('role', 'button');
      overlay.setAttribute('aria-label', t('home.tapToStart'));
      overlay.style.cssText = `
        position: fixed; inset: 0; z-index: 9999;
        display: flex; flex-direction: column;
        align-items: center; justify-content: flex-end;
        padding-bottom: calc(var(--nav-height, 80px) + env(safe-area-inset-bottom, 0px) + 40px);
        background: rgba(0,0,0,0.05);
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
        animation: tapOverlayFadeIn 0.4s ease;
      `;

      const hint = document.createElement('div');
      hint.style.cssText = `
        padding: 14px 28px;
        border-radius: 999px;
        background: rgba(61,157,143,0.92);
        color: #fff;
        font-size: 16px;
        font-weight: 600;
        font-family: var(--font-family, system-ui, sans-serif);
        box-shadow: 0 4px 20px rgba(61,157,143,0.4);
        animation: tapHintPulse 2s ease-in-out infinite;
        pointer-events: none;
      `;
      hint.textContent = t('overlay.tapToBegin');
      overlay.appendChild(hint);

      // Inject animations
      if (!document.getElementById('tap-overlay-style')) {
        const style = document.createElement('style');
        style.id = 'tap-overlay-style';
        style.textContent = `
          @keyframes tapOverlayFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes tapHintPulse {
            0%, 100% { transform: scale(1); opacity: 0.95; }
            50% { transform: scale(1.04); opacity: 1; }
          }
        `;
        document.head.appendChild(style);
      }

      function dismissOverlay(): void {
        // The tap/click event will bubble through to the document-level
        // listeners in tts.ts (onFirstInteractionForAudio), which will
        // play the pending audio. We just need to remove our overlay.
        overlay.style.animation = 'none';
        overlay.style.opacity = '0';
        overlay.style.transition = 'opacity 0.3s ease';
        setTimeout(() => overlay.remove(), 350);
        overlay.removeEventListener('click', dismissOverlay);
        overlay.removeEventListener('touchend', dismissOverlay);
      }

      // Use touchend + click (touchend fires first on mobile, click for desktop)
      overlay.addEventListener('touchend', dismissOverlay, { once: true });
      overlay.addEventListener('click', dismissOverlay, { once: true });

      document.body.appendChild(overlay);

      // Auto-remove overlay after 15s if user hasn't tapped (they may have
      // interacted elsewhere that already triggered the audio listeners)
      setTimeout(() => {
        if (document.getElementById('tap-to-begin')) {
          overlay.style.opacity = '0';
          overlay.style.transition = 'opacity 0.5s ease';
          setTimeout(() => overlay.remove(), 500);
        }
      }, 15000);
    }, 600); // Check 600ms after speakGreeting — enough for play() promise to reject
  }, 2500);

  // Show PWA install prompt (platform-aware, respects dismissal)
  showInstallPrompt();
}

// -----------------------------------------------------------------------
// Entry point
// -----------------------------------------------------------------------

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initApp().catch(console.error);
  });
} else {
  initApp().catch(console.error);
}
